/**
 * Semantic Design Tokens
 *
 * These tokens carry meaning and provide context for how primitives should be used.
 * They reference primitive tokens and define the purpose/intent of design decisions.
 */

import {
  blobSize,
  colorBlue,
  colorGreen,
  colorNeutral,
  colorPurple,
  colorRed,
  colorWarm,
  opacity,
} from './primitive'

// All available theme modes
export const THEME_MODES = ['warm', 'red', 'blue', 'green', 'purple'] as const

// Theme mode type derived from the array
export type ThemeMode = (typeof THEME_MODES)[number]

// Accent Color Palettes by Theme - varied in hue for visual distinction
export const accentPalettes = {
  warm: [colorWarm[500], colorWarm[800], colorWarm[600], colorWarm[900], colorWarm[700]], // Orange to pink span
  red: [colorRed[500], colorRed[900], colorRed[600], colorRed[800], colorRed[700]], // Mixed: Deep Burgundy (md), Orange (sm), Bright Burgundy (2xl), Coral-Orange (xl), Coral (lg)
  blue: [colorBlue[500], colorBlue[700], colorBlue[900], colorBlue[600], colorBlue[800]], // Cyan → Blue → Indigo gradient
  green: [colorGreen[500], colorGreen[600], colorGreen[800], colorGreen[700], colorGreen[900]], // Lime → Emerald → Cyan gradient
  purple: [
    colorPurple[500],
    colorPurple[600],
    colorPurple[700],
    colorPurple[800],
    colorPurple[900],
  ], // Light blue → Violet gradient
} as const

// Semantic Color Tokens
export const semanticColors = {
  // Accent colors (theme-dependent)
  accentPrimary: 'var(--color-accent-primary)',
  accentSecondary: 'var(--color-accent-secondary)',

  // Background ambient colors (used for gradients, blobs, etc.)
  backgroundAmbient1: 'var(--color-background-ambient-1)',
  backgroundAmbient2: 'var(--color-background-ambient-2)',
  backgroundAmbient3: 'var(--color-background-ambient-3)',
  backgroundAmbient4: 'var(--color-background-ambient-4)',
  backgroundAmbient5: 'var(--color-background-ambient-5)',

  // Static semantic colors
  backgroundPrimary: colorNeutral.gray50,
  backgroundSecondary: colorNeutral.white,
  textPrimary: colorNeutral.black,
  textSecondary: colorNeutral.gray600,
  borderDefault: colorNeutral.gray200,
} as const

// Blob Configuration Semantic Tokens
export const blobSemantics = {
  sizes: [blobSize.md, blobSize.sm, blobSize['2xl'], blobSize.xl, blobSize.lg],
  opacities: [opacity[75], opacity[80], opacity[60], opacity[65], opacity[70]],
} as const

// Get theme-specific color palette
export function getThemePalette(theme: ThemeMode): readonly string[] {
  return accentPalettes[theme]
}

// Generate CSS variable definitions for a theme
export function generateThemeCSSVariables(theme: ThemeMode): Record<string, string> {
  const palette = getThemePalette(theme)

  return {
    '--color-accent-primary': palette[0],
    '--color-accent-secondary': palette[1],
    '--color-background-ambient-1': palette[0],
    '--color-background-ambient-2': palette[1],
    '--color-background-ambient-3': palette[2],
    '--color-background-ambient-4': palette[3],
    '--color-background-ambient-5': palette[4],
  }
}
